import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  {
    question: 'How do I claim my player profile?',
    answer:
      'Claim your player profile, complete your registration process and fill in your profile details including bio information, highlights, and academic information. Your profile will be visible to coaches and scouts across our platform.',
  },
  {
    question: 'How can coaches find and recruit players?',
    answer:
      'Coaches can use our uncommitted tracker, statistics filters, and player profiles to discover JUCO talent. You can follow players to build your team and reach out directly through our messaging system.',
  },
{
    question: 'How do I upload game highlights or video clips?',
    answer:
      'Navigate to your player profile and use the video upload section. You can upload clips directly or link to external video platforms. Coaches and scouts will see your highlights when they view your profile.',
  },
{
    question: 'How do I reset my password?',
    answer:
      'Click "Forgot Password" on the login page and enter your registered email address. You\'ll receive a reset link to create a new password. If you don\'t receive the email, check your spam folder or contact support.',
  },
  {
    question: 'Is my personal information secure?',
    answer:
      'Absolutely. We use industry-standard encryption and security practices to protect your data. Your personal information is only shared according to your privacy settings and is never sold to third parties.',
  },
  {
    question: 'How do I cancel or change my subscription?',
    answer:
      'You can manage your subscription from your profile settings. If you need assistance with billing or plan changes, reach out to our support team using the contact form below.',
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => (
  <div
    className={`rounded-xl border overflow-hidden transition-colors duration-300 ${
      isOpen ? 'border-jp-accent bg-jp-card shadow-md' : 'border-jp-outline/50 bg-jp-section hover:border-jp-accent/60'
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center gap-4 px-5 py-4 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
    >
      <span
        className={`text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
          isOpen ? 'bg-jp-accent text-jp-navy' : 'bg-jp-card border border-jp-line text-jp-muted'
        }`}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <span className="text-sm sm:text-base font-semibold flex-1 text-jp-ink">{question}</span>
      <svg
        className={`w-5 h-5 flex-shrink-0 text-jp-accent transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
      <p className="px-5 pb-5 pl-16 text-sm text-jp-muted leading-relaxed">{answer}</p>
    </div>
  </div>
);

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition text-sm';

const QUICK_LINKS = [
  { to: '/about', label: 'About Us', desc: 'Our mission & story' },
  { to: '/pricing', label: 'Pricing', desc: 'Plans & subscriptions' },
  { to: '/for-players', label: 'For Players', desc: 'Get discovered' },
  { to: '/for-coaches', label: 'For Coaches', desc: 'Recruiting tools' },
];

const SupportPage = ({ isDashboard = false }) => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: integrate with backend support API
    setFormSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  return (
    <main className={`min-h-screen text-jp-ink text-left overflow-x-hidden ${isDashboard ? '' : 'bg-jp-page'}`}>
      {/* Header band: navy in both themes so the white navbar stays readable */}
      <section
        className={`bg-jp-band px-4 sm:px-6 lg:px-8 ${
          isDashboard ? 'pt-10 pb-10 rounded-b-2xl' : 'pt-32 sm:pt-36 pb-14 sm:pb-16'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-yellow-300 text-sm sm:text-base font-bold uppercase tracking-widest mb-4">
            We're Here To Help
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-anton leading-[1.1] tracking-tight text-white">
            SUPPORT{' '}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
              CENTER
            </span>
          </h1>
          <p className="mt-4 text-slate-300 text-sm sm:text-base max-w-2xl">
            Got questions? We've got answers. Reach out anytime — we're here to help you
            make the most of JUCO Pipeline.
          </p>
        </div>
      </section>

      {/* FAQ + Contact Form */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT — FAQ */}
          <div>
            <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
              Click any question to expand
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-6">
              Frequently Asked <span className="text-jp-accent">Questions</span>
            </h2>
            <div className="bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4 space-y-2.5">
              {faqData.map((faq, index) => (
                <FAQItem
                  key={index}
                  index={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === index}
                  onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — Contact Form */}
          <div>
            <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
              We'll get back within 24 hours
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-6">
              Send Us a <span className="text-jp-accent">Message</span>
            </h2>
            <div className="bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4">
              <div className="rounded-xl p-5 sm:p-6 bg-jp-section border border-jp-outline/50">
                {formSubmitted && (
                  <div className="mb-5 px-4 py-3 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="support-name" className="block text-sm font-semibold text-jp-ink mb-1.5">Full Name</label>
                    <input
                      id="support-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="support-email" className="block text-sm font-semibold text-jp-ink mb-1.5">Email Address</label>
                    <input
                      id="support-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="support-subject" className="block text-sm font-semibold text-jp-ink mb-1.5">Subject</label>
                    <input
                      id="support-subject"
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What's this about?"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="support-message" className="block text-sm font-semibold text-jp-ink mb-1.5">Message</label>
                    <textarea
                      id="support-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      placeholder="Describe your issue or question..."
                      className={`${inputClass} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 text-sm font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98]"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom — Contact Info Strip */}
        {/* <div className="rounded-2xl bg-gradient-to-r from-slate-800/50 via-slate-800/30 to-slate-800/50 border border-slate-700/40 p-6 sm:p-8 mb-10 backdrop-blur-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/15 to-blue-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Email Us</h4>
                <a href="mailto:support@jucopipeline.com" className="text-cyan-400 text-sm hover:underline break-all">
                  support@jucopipeline.com
                </a>
              </div>
            </div>

      
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/15 to-blue-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Call Us</h4>
                <a href="tel:+1234567890" className="text-cyan-400 text-sm hover:underline">
                  (123) 456-7890
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/15 to-blue-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Business Hours</h4>
                <p className="text-gray-400 text-sm">Mon - Fri: 9AM - 6PM EST</p>
              </div>
            </div>

          
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/15 to-blue-500/15 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Follow Us</h4>
                <div className="flex items-center gap-2">
                  <a href="#" className="w-8 h-8 rounded-lg bg-slate-700/50 hover:bg-cyan-400/20 flex items-center justify-center text-gray-400 hover:text-cyan-400 transition border border-slate-600/40 hover:border-cyan-500/40">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
                  </a>
                  <a href="#" className="w-8 h-8 rounded-lg bg-slate-700/50 hover:bg-cyan-400/20 flex items-center justify-center text-gray-400 hover:text-cyan-400 transition border border-slate-600/40 hover:border-cyan-500/40">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="#" className="w-8 h-8 rounded-lg bg-slate-700/50 hover:bg-cyan-400/20 flex items-center justify-center text-gray-400 hover:text-cyan-400 transition border border-slate-600/40 hover:border-cyan-500/40">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div> */}

      {/* Quick Links */}
      <section className="bg-jp-section border-t border-jp-line px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <p className="text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest mb-2">
            Keep Exploring
          </p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-6">
            Quick <span className="text-jp-accent">Links</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group rounded-xl p-5 bg-jp-section border border-jp-outline/50 hover:border-jp-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
              >
                <h3 className="text-sm font-bold text-jp-ink group-hover:text-jp-accent transition-colors">
                  {link.label}
                </h3>
                <p className="text-xs text-jp-muted mt-1">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default SupportPage;
